import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useEffect, useState } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, Plus, History, Notebook } from 'lucide-react';
import { PlanDuracionConfig, fetchPlanDuracionConfigs } from '../../../services/planconfigService';
import AddPlanConfigModal from '../../../components/ConfigPlan/AddPlanConfigModal';
import EditPlanConfigModal from '../../../components/ConfigPlan/EditPlanConfigModal';
import DeletePlanConfigModal from '../../../components/ConfigPlan/DeletePlanConfigModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const sortAscending = (configs: PlanDuracionConfig[]) =>
    [...configs].sort((a, b) => a.id - b.id);

const ExpandableDescription = ({ description }: { description?: string | null }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const words = (description || '').split(' ');
    const truncatedDescription = words.slice(0, 4).join(' ') + (words.length > 4 ? '...' : '');

    if (!description) return <span className="text-gray-400">Sin descripción</span>;

    return (
        <span
            onClick={() => words.length > 4 && setIsExpanded(!isExpanded)}
            className={words.length > 4 ? 'cursor-pointer hover:text-blue-600 transition-colors' : 'cursor-default'}
        >
            {isExpanded ? description : truncatedDescription}
        </span>
    );
};

const PlanConfigPage: React.FC = () => {
    const [configuraciones, setConfiguraciones] = useState<PlanDuracionConfig[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<PlanDuracionConfig | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();

    // Auth & permissions
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('planduracionconfig', userLevelId);

    // Carga de configuraciones solo si tiene permiso de lectura
    useEffect(() => {
        const cargarConfiguraciones = async () => {
            try {
                if (perm?.can_read) {
                    const data = await fetchPlanDuracionConfigs();
                    const sortedData = sortAscending(data);   // orden ascendente por ID
                    setConfiguraciones(sortedData);
                } else {
                    setConfiguraciones([]);
                }
            } catch (error) {
                /* Browser diagnostics omitted. */
                setConfiguraciones([]);
            }
        };
        if (perm) cargarConfiguraciones();
    }, [perm]);

    const handleCreated = (newConfig: PlanDuracionConfig) => {
        if (!perm?.can_read) return;
        setConfiguraciones(prev => sortAscending([...prev, newConfig]));
        setShowModal(false);
    };

    const handleUpdated = (updatedConfig: PlanDuracionConfig) => {
        setConfiguraciones(prev => {
            const updatedList = prev.map(c => c.id === updatedConfig.id ? updatedConfig : c);
            return sortAscending(updatedList);
        });
    };

    const handleDeleted = (deletedId: number) => {
        setConfiguraciones(prev => prev.filter(c => c.id !== deletedId));
    };

    // Paginación
    const totalPages = Math.ceil(configuraciones.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfigs = configuraciones.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando configuración de planes..." />;

    return <><LumaResourceWorkspace title="Configuración de Planes" description="Duraciones y reglas de reserva" group="Planes" rows={configuraciones} columns={[{key:'plan_nombre',label:'Plan'},{key:'unidad',label:'Unidad'},{key:'dias',label:'Días'},{key:'horas',label:'Horas'},{key:'estado',label:'Estado'}]} idKey="id" nameKey="plan_nombre" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setShowModal(true)}:undefined} onEdit={perm?.can_update?r=>{setSelectedConfig(r);setEditModalOpen(true);}:undefined} onDelete={perm?.can_delete?r=>{setSelectedConfig(r);setDeleteModalOpen(true);}:undefined} history=""/><AddPlanConfigModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    onCreated={handleCreated}
                /><EditPlanConfigModal
                    isOpen={editModalOpen}
                    config={selectedConfig}
                    onClose={() => setEditModalOpen(false)}
                    onUpdated={handleUpdated}
                /><DeletePlanConfigModal
                    isOpen={deleteModalOpen}
                    config={selectedConfig}
                    onClose={() => setDeleteModalOpen(false)}
                    onDeleted={handleDeleted}
                /><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                /></>;
};

export default PlanConfigPage;
